import type { Signal } from '@angular/core';
// Specific modules rather than the public API, so the plain-logic test runner can load this file.
import { AGENT_TYPE } from 'ag-common-lib/lib/lists/agent-type.enum';
import { Entity } from 'ag-common-lib/lib/lists/entity-permission.enum';
import { AgentKeys } from 'ag-common-lib/lib/models/domain/agent.keys';
import type { RoleEntityPermissionMap } from 'ag-common-lib/lib/models/utils/entity-permission.model';
import {
  ReportIds,
  ReportingComparisonScopes,
  type ReportId,
  type ReportingComparisonScope,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { hasPermissionGuard } from '../../shared/utils/has-permission-guard';

/** The report sections: Alliance is ARM's company-wide view; RMD and MGA are the portal's scoped views. */
export type ReportsSectionId = 'alliance' | 'rmd' | 'mga';

export type ReportsViewerTile =
  | { kind: 'report'; reportId: ReportId; name: string; description: string }
  | { kind: 'comparison'; name: string; description: string }
  | { kind: 'productionTracking'; name: string; description: string };

export type ReportsViewerTileGroup = {
  /** Heading above the group, e.g. "Bulletin Reports"; null renders the tiles ungrouped. */
  title: string | null;
  tiles: ReportsViewerTile[];
};

export type ReportsViewerSection = {
  id: ReportsSectionId;
  title: string;
  /** Sent with every request from this section; the reporting API limits the data to it. */
  scope: ReportingComparisonScope;
  groups: ReportsViewerTileGroup[];
};

export const REPORT_SCOPE_LABELS: Record<ReportingComparisonScope, string> = {
  [ReportingComparisonScopes.allianceGroup]: 'Alliance Group',
  [ReportingComparisonScopes.mga]: 'MGA/Agency',
  [ReportingComparisonScopes.rmd]: 'RMD',
};

export const REPORT_SECTION_ID_BY_SCOPE: Record<ReportingComparisonScope, ReportsSectionId> = {
  [ReportingComparisonScopes.allianceGroup]: 'alliance',
  [ReportingComparisonScopes.rmd]: 'rmd',
  [ReportingComparisonScopes.mga]: 'mga',
};

const REPORT_ENTITY_BY_ID: Record<ReportId, Entity> = {
  [ReportIds.mgaPaid]: Entity.reportsViewerMgaPaid,
  [ReportIds.personalPaid]: Entity.reportsViewerPersonalPaid,
  [ReportIds.rmdPaid]: Entity.reportsViewerRmdPaid,
  [ReportIds.mgaSubmit]: Entity.reportsViewerMgaSubmitted,
  [ReportIds.personalSubmit]: Entity.reportsViewerPersonalSubmitted,
  [ReportIds.rmdSubmit]: Entity.reportsViewerRmdSubmitted,
};

/** The permission a tile's destination needs; the reporting API checks the same one. */
export function getReportsViewerTileEntity(tile: ReportsViewerTile): Entity {
  if (tile.kind === 'report') {
    return REPORT_ENTITY_BY_ID[tile.reportId];
  }

  return tile.kind === 'comparison' ? Entity.reportsViewerComparison : Entity.reportsViewerProductionHistory;
}

const report = (reportId: ReportId, name: string, description: string): ReportsViewerTile => ({
  kind: 'report',
  reportId,
  name,
  description,
});

const BULLETIN_REPORTS = 'Bulletin Reports';

const productionTracking = (description: string): ReportsViewerTile => ({
  kind: 'productionTracking',
  name: 'Production Tracking',
  description,
});

const comparison = (description: string): ReportsViewerTile => ({
  kind: 'comparison',
  name: 'Comparison',
  description,
});

const COMPARISON_DESCRIPTION =
  'Compare current paid and submitted results with an earlier period using totals, trends, and biggest movers.';

/** Every report section, in display order. Each app shows the ones its viewer may see. */
export const REPORTS_VIEWER_SECTIONS: readonly ReportsViewerSection[] = [
  {
    id: 'alliance',
    title: 'Alliance Group Reports',
    scope: ReportingComparisonScopes.allianceGroup,
    groups: [
      {
        title: BULLETIN_REPORTS,
        tiles: [
          report(ReportIds.mgaPaid, 'Paid – MGA', 'Year-to-date paid life insurance production, by MGA.'),
          report(ReportIds.mgaSubmit, 'Submitted – MGA', 'Weekly submitted life insurance business, by MGA.'),
          report(ReportIds.personalPaid, 'Paid – Personal Production', 'Year-to-date paid production, by agent.'),
          report(ReportIds.personalSubmit, 'Submitted – Personal Production', 'Weekly submitted business, by agent.'),
          report(ReportIds.rmdPaid, 'Paid – RMD', 'Year-to-date paid life insurance production, by RMD.'),
          report(ReportIds.rmdSubmit, 'Submitted – RMD', 'Weekly submitted life insurance business, by RMD.'),
        ],
      },
      {
        title: null,
        tiles: [
          productionTracking('Review Alliance Group production history for trend and historical analysis.'),
          comparison(COMPARISON_DESCRIPTION),
        ],
      },
    ],
  },
  {
    id: 'rmd',
    title: 'RMD Specific Reports',
    scope: ReportingComparisonScopes.rmd,
    groups: [
      {
        title: BULLETIN_REPORTS,
        tiles: [
          report(ReportIds.mgaPaid, 'Paid – MGA', 'Year-to-date paid production for your MGAs, by MGA.'),
          report(ReportIds.mgaSubmit, 'Submitted – MGA', 'Weekly submitted business for your MGAs, by MGA.'),
          report(
            ReportIds.personalPaid,
            'Paid – Personal Production',
            'Year-to-date paid production for your MGAs, by agent.',
          ),
          report(
            ReportIds.personalSubmit,
            'Submitted – Personal Production',
            'Weekly submitted business for your MGAs, by agent.',
          ),
        ],
      },
      {
        title: null,
        tiles: [productionTracking('Review production history for your MGAs.'), comparison(COMPARISON_DESCRIPTION)],
      },
    ],
  },
  {
    id: 'mga',
    title: 'MGA Specific Reports',
    scope: ReportingComparisonScopes.mga,
    groups: [
      {
        title: BULLETIN_REPORTS,
        tiles: [
          report(
            ReportIds.personalPaid,
            'Paid – Personal Production',
            'Year-to-date paid production for your agency, by agent.',
          ),
          report(
            ReportIds.personalSubmit,
            'Submitted – Personal Production',
            'Weekly submitted business for your agency, by agent.',
          ),
        ],
      },
      {
        title: null,
        tiles: [productionTracking('Review production history for your agency.'), comparison(COMPARISON_DESCRIPTION)],
      },
    ],
  },
];

const MGA_SECTION_AGENT_TYPES = new Set<string>([AGENT_TYPE.MGA_ADMIN, AGENT_TYPE.MGA_PRINCIPAL]);

/**
 * The scoped sections an agent belongs to: RMD for RMDs, MGA for MGA principals and admins.
 * The reporting API applies the same rule, so a section shown here is one it will serve.
 */
export function buildReportSectionAccess(agent: Record<string, any> | null | undefined): Set<ReportsSectionId> {
  const sections = new Set<ReportsSectionId>();

  if (agent?.[AgentKeys.is_rmd] === true) {
    sections.add('rmd');
  }

  if (MGA_SECTION_AGENT_TYPES.has(agent?.[AgentKeys.agent_type])) {
    sections.add('mga');
  }

  return sections;
}

export type ReportAccessSource = {
  visibleSectionsSig: Signal<ReadonlySet<ReportsSectionId>>;
  permissionsSig: Signal<RoleEntityPermissionMap | null>;
};

/**
 * Whether the viewer may open a page for this scope that needs `entity`: the app shows the
 * scope's section, and the viewer holds the entity. Read inside a computed to stay reactive.
 */
export function canSeeReportScope(
  source: ReportAccessSource,
  scope: ReportingComparisonScope | null,
  entity: Entity,
): boolean {
  if (!scope) {
    return false;
  }

  return (
    source.visibleSectionsSig().has(REPORT_SECTION_ID_BY_SCOPE[scope]) &&
    hasPermissionGuard([entity], source.permissionsSig() ?? {})
  );
}

/**
 * The scope in a route parameter, or null for a value that isn't one. `personal` is the old
 * name for the MGA scope.
 */
export function parseReportScopeParam(value: string | null | undefined): ReportingComparisonScope | null {
  if (value === 'personal') {
    return ReportingComparisonScopes.mga;
  }

  const scopes: readonly string[] = Object.values(ReportingComparisonScopes);
  return value && scopes.includes(value) ? (value as ReportingComparisonScope) : null;
}
